import type { Id } from "@convex/_generated/dataModel";
import type { ProductIntent } from "@/lib/products/types";
import { safeLocalStorage } from "@/lib/storage/safe-storage";

const STORAGE_KEY = "repackify:recently-viewed:v1";
const MAX_ITEMS = 12;

export interface RecentlyViewedItem {
  _id: Id<"products">;
  title: string;
  slug?: string | null;
  primaryImageUrl?: string | null;
  buyerUnitPriceCents?: number | null;
  publicIdentifier?: string | null;
  cityName?: string | null;
  stateAbbr?: string | null;
  originZip?: string | null;
  intent?: ProductIntent | null;
  viewedAt: number;
}

export const RECENTLY_VIEWED_UPDATE_EVENT =
  "repackify:recently-viewed:update" as const;

function safeParse(raw: string | null): RecentlyViewedItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (it): it is RecentlyViewedItem =>
        it != null &&
        typeof it === "object" &&
        typeof it._id === "string" &&
        typeof it.title === "string" &&
        typeof it.viewedAt === "number",
    );
  } catch {
    return [];
  }
}

export function readRecentlyViewed(): RecentlyViewedItem[] {
  return safeParse(safeLocalStorage().getItem(STORAGE_KEY));
}

export function addRecentlyViewed(
  item: Omit<RecentlyViewedItem, "viewedAt">,
): void {
  if (typeof window === "undefined") return;
  const existing = readRecentlyViewed().filter((i) => i._id !== item._id);
  const next: RecentlyViewedItem[] = [
    { ...item, viewedAt: Date.now() },
    ...existing,
  ].slice(0, MAX_ITEMS);
  safeLocalStorage().setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(RECENTLY_VIEWED_UPDATE_EVENT));
}

export function clearRecentlyViewed(): void {
  if (typeof window === "undefined") return;
  safeLocalStorage().removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(RECENTLY_VIEWED_UPDATE_EVENT));
}
